import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, AlertMessage } from "components/atoms";
import { TextInput } from "components/molecules";
import { useAuth } from "contexts";

const ConfirmSignUpSchema = Yup.object().shape({
  confirmationCode: Yup.string()
    .max(6, "Confirmation code must be 6 characters")
    .required("Confirmation code is required"),
});

const ConfirmSignUpForm: React.FC = () => {
  const navigate = useNavigate();
  const {
    authState,
    handleSignUpConfirmation,
    resendConfirmationCode,
    handleSignIn,
  } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleConfirmationError = (error: unknown): void => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    setErrorMessage(errorMessage);
  };

  const checkUsername = (): string | null => {
    const username = authState?.temporaryData?.username;
    if (!username) {
      handleConfirmationError("Session expired. Please sign up again.");
      return null;
    }
    return username;
  };

  const handleSuccessfulConfirmation = (): void => {
    confirmSignUpFormik.resetForm();

    if (authState.nextStep === "DONE") {
      navigate("/");
      window.location.reload();
    }
  };

  const handleConfirmSignUpSubmit = async (values: {
    confirmationCode: string;
  }) => {
    try {
      const username = checkUsername();
      if (!username) {
        return;
      }

      const signUpError = await handleSignUpConfirmation({
        username,
        confirmationCode: values.confirmationCode.toString(),
      });

      if (signUpError) {
        handleConfirmationError(signUpError);
        return;
      }

      const password = authState.temporaryData?.signUpPassword;
      if (!password) {
        handleConfirmationError("Session expired. Please sign up again.");
        return;
      }

      const signInError = await handleSignIn({
        username,
        password,
      });

      if (signInError) {
        handleConfirmationError(signInError);
        return;
      }

      handleSuccessfulConfirmation();
    } catch (error) {
      handleConfirmationError(error);
    }
  };

  const confirmSignUpFormik = useFormik({
    initialValues: {
      confirmationCode: "",
    },
    validationSchema: ConfirmSignUpSchema,
    onSubmit: handleConfirmSignUpSubmit,
  });

  const handleResendCode = async (): Promise<void> => {
    try {
      const username = checkUsername();
      if (!username) {
        return;
      }

      const error = await resendConfirmationCode(username);

      if (error) {
        handleConfirmationError(error);
      }
    } catch (error) {
      handleConfirmationError(error);
    }
  };

  const maskEmail = (email: string | undefined): string => {
    if (!email || !email.includes("@")) {
      return "";
    }

    const [username, domain] = email.split("@");

    if (username.length < 2 || domain.length < 2) {
      return "";
    }

    const maskedUsername = username.charAt(0) + "*".repeat(3);
    const maskedDomain = domain.charAt(0) + "*".repeat(3);
    return maskedUsername + "@" + maskedDomain;
  };

  return (
    <form className="auth-form" onSubmit={confirmSignUpFormik.handleSubmit}>
      <div className="form-group">
        <h2>Confirm Sign Up</h2>
        <p>
          Your code is on the way. To log in, enter the code we emailed to{" "}
          {maskEmail(authState?.temporaryData?.username)}. It may take a minute
          to arrive.
        </p>
      </div>

      <TextInput
        label="Confirmation Code"
        id="confirmationCode"
        placeholder="Enter the code"
        inputMode="numeric"
        maxLength={6}
        getFieldProps={confirmSignUpFormik.getFieldProps}
        touched={confirmSignUpFormik.touched.confirmationCode}
        error={confirmSignUpFormik.errors.confirmationCode}
      />

      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}

      <Button
        type="submit"
        text="Confirm"
        className="form-button"
        width="100%"
        variant="primary"
      />

      <Button
        type="button"
        text="Resend Code"
        onClick={handleResendCode}
        className="form-button"
        width="100%"
        variant="border"
      />
    </form>
  );
};

export default ConfirmSignUpForm;
